import { Check, Plus, Trash } from "@phosphor-icons/react"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { IconButton } from "@tokenterminal/core/ui/button/IconButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Checkbox } from "@tokenterminal/core/ui/input/checkbox/Checkbox"
import { Field } from "@tokenterminal/core/ui/input/Field"
import {
  ToggleButton,
  ToggleButtonGroup,
} from "@tokenterminal/core/ui/input/toggle/ToggleButtonGroup"

import { Card } from "@tokenterminal/core/ui/layout/card/Card"
import { Divider } from "@tokenterminal/core/ui/layout/divider/Divider"
import { MenuButton } from "@tokenterminal/core/ui/menu/MenuButton"
import { type SetStateAction, type Dispatch, useRef } from "react"
import { twMerge } from "tailwind-merge"
import BackgroundColorSelector from "./sidebar/BackgroundColorSelector"
import { CustomImageUpload } from "./sidebar/CustomImageUpload"
import { ProjectLogoDropdown } from "./sidebar/ProjectLogoDropdown"
import {
  type ChartImageModalProps,
  presets,
  transitionArgs,
  type ChartImageState,
} from "./types"

const CollapsibleSection = ({
  children,
  title,
  isOpen,
  onAdd,
  onRemove,
}: {
  children: React.ReactNode
  title: string
  isOpen: boolean
  onAdd: () => void
  onRemove: () => void
}) => {
  const header = (
    <div className="flex items-center gap-2 justify-between w-full py-1 pl-3 pr-1">
      <label
        className={twMerge(
          "tt-field-label",
          isOpen ? "text-fg-default" : "text-fg-secondary"
        )}
      >
        {title}
      </label>
      {isOpen ? (
        <IconButton
          variant="ghost"
          onPress={onRemove}
          icon={Trash}
          size="small"
          label="Remove logo"
          className="text-fg-secondary"
        />
      ) : (
        <div className="w-tt-small h-tt-small flex items-center justify-center">
          <Icon as={Plus} />
        </div>
      )}
    </div>
  )

  return (
    <div className="flex flex-col w-full">
      <div className="p-1 w-full">
        {isOpen ? (
          <div className="border border-solid border-transparent">{header}</div>
        ) : (
          <Button
            variant="ghost"
            onPress={onAdd}
            className="!px-0 !py-0 w-full !h-auto"
          >
            {header}
          </Button>
        )}
      </div>
      {isOpen && <div className="px-4 pb-4 -mt-1 w-full">{children}</div>}
    </div>
  )
}

export const ChartConfigSidebar = ({
  state,
  setState,
  sourcesAndDefinitionsText,
  footnoteText,
  setShowSettings,
  selectPreset,
  isPresetDirty,
  resetActivePreset,
  defaultProject,
  canvasHeight,
  setStoredInitialPreset,
}: {
  state: ChartImageState
  setState: Dispatch<SetStateAction<ChartImageState>>
  sourcesAndDefinitionsText?: string
  footnoteText?: string
  setShowSettings: (showSettings: boolean) => void
  selectPreset: (presetId: string) => void
  isPresetDirty: boolean
  resetActivePreset: () => void
  defaultProject?: ChartImageModalProps["defaultProject"]
  canvasHeight: number
  setStoredInitialPreset: (presetId: string) => void
}) => {
  const sidebarRef = useRef<HTMLDivElement>(null)

  const handleOpenProjectLogoDropdown = () => {
    const button = sidebarRef.current?.querySelector(
      "[data-project-logo-dropdown]"
    )
    if (button) {
      ;(button as HTMLButtonElement).click()
    }
  }

  return (
    <Card
      ref={sidebarRef}
      className="rounded-xl p-0 flex flex-col w-[350px] overflow-y-auto z-10"
      style={{
        height: `calc(${canvasHeight}px)`,
        transition: `height ${transitionArgs}`,
      }}
    >
      <div className="flex items-center justify-between gap-2 pl-2 pr-4 py-2">
        <MenuButton
          trigger={
            <Button
              variant="ghost"
              className="flex items-center gap-2 !px-1.5 !py-1 !h-auto"
            >
              <div className="flex flex-col leading-none">
                <div className="flex items-center gap-2">
                  <div className="text-base tracking-tight font-medium text-fg-default">
                    {state.presetId
                      ? presets[state.presetId]?.label
                      : "Select preset"}
                  </div>
                  <MenuButton.DropdownIndicator />
                </div>
              </div>
            </Button>
          }
        >
          {Object.entries(presets).map(([key, preset]) => (
            <MenuButton.Item
              key={preset.id}
              onAction={() => {
                selectPreset(preset.id)
                setStoredInitialPreset(preset.id)
              }}
            >
              <div className="flex items-center gap-2">
                <Icon
                  as={Check}
                  className="text-fg-secondary"
                  style={{
                    opacity: preset.id === state.presetId ? 1 : 0,
                  }}
                />
                {preset.label}
              </div>
            </MenuButton.Item>
          ))}
        </MenuButton>
        <div className="flex items-center gap-2">
          {isPresetDirty && (
            <button
              onClick={resetActivePreset}
              className="text-sm text-fg-secondary hover:underline"
            >
              Reset
            </button>
          )}
        </div>
      </div>
      <Divider className="shrink-0" />
      <div className="pt-3 px-4 pb-4 flex flex-col gap-4">
        <Field label="Aspect ratio">
          <ToggleButtonGroup
            selectedKey={state.aspectRatio}
            onSelectionChange={(key) => {
              setState({
                ...state,
                aspectRatio: key as ChartImageState["aspectRatio"],
              })
            }}
            className="w-full"
            size="small"
          >
            <ToggleButton className="w-full" id="16/9">
              Horizontal
            </ToggleButton>
            <ToggleButton className="w-full" id="1/1">
              Square
            </ToggleButton>
          </ToggleButtonGroup>
        </Field>
        <Field label="Padding">
          <ToggleButtonGroup
            selectedKey={state.padding.toString()}
            onSelectionChange={(key) => {
              setState({
                ...state,
                padding: key as ChartImageState["padding"],
              })
            }}
            size="small"
            className="w-full"
          >
            <ToggleButton className="w-full" id="small">
              Small
            </ToggleButton>
            <ToggleButton className="w-full" id="medium">
              Medium
            </ToggleButton>
            <ToggleButton className="w-full" id="large">
              Large
            </ToggleButton>
          </ToggleButtonGroup>
        </Field>
      </div>
      <Divider className="shrink-0" />
      <CollapsibleSection
        title="Logo"
        onRemove={() => setState({ ...state, image: { type: "none" } })}
        isOpen={state.image?.type !== "none"}
        onAdd={() => {
          setState({
            ...state,
            image: {
              type: "project",
              projectId: defaultProject?.project_id,
              projectName: defaultProject?.name,
              projectLogo: defaultProject?.logo,
            },
          })
          if (!defaultProject) {
            setTimeout(() => {
              handleOpenProjectLogoDropdown()
            }, 10)
          }
        }}
      >
        <div className="flex flex-col gap-2">
          <ToggleButtonGroup
            selectedKey={state.image?.type}
            onSelectionChange={(key) => {
              setState({
                ...state,
                image: {
                  ...state.image,
                  type: key as "project" | "custom",
                  projectId: undefined,
                  projectLogo: undefined,
                  customUrl: undefined,
                },
              })
            }}
            className="w-full"
          >
            <ToggleButton className="w-full" id="project">
              Project logo
            </ToggleButton>
            <ToggleButton className="w-full" id="custom">
              Custom
            </ToggleButton>
          </ToggleButtonGroup>

          {state.image?.type === "project" && (
            <ProjectLogoDropdown state={state} setState={setState} />
          )}
          {state.image?.type === "custom" && (
            <div className="mt-2">
              <CustomImageUpload
                // @ts-ignore
                file={state.image.customUrl}
                setFile={(file) => {
                  setState({
                    ...state,
                    image: {
                      ...state.image,
                      type: "custom",
                      customUrl: file ?? undefined,
                    },
                  })
                }}
              />
            </div>
          )}
        </div>
      </CollapsibleSection>
      <Divider className="shrink-0" />

      <CollapsibleSection
        title="Background"
        isOpen={state.showBackground}
        onRemove={() =>
          setState({
            ...state,
            showBackground: false,
            sourcesAndFootnoteDisplay: "inside-card",
          })
        }
        onAdd={() => setState({ ...state, showBackground: true })}
      >
        <BackgroundColorSelector state={state} setState={setState} />
      </CollapsibleSection>
      <Divider className="shrink-0" />
      {sourcesAndDefinitionsText && (
        <CollapsibleSection
          title="Sources & footnote"
          isOpen={state.sourcesAndFootnoteDisplay !== null}
          onRemove={() =>
            setState({ ...state, sourcesAndFootnoteDisplay: null })
          }
          onAdd={() =>
            setState({
              ...state,
              sourcesAndFootnoteDisplay: "inside-card",
              showSources: true,
              // Only default to showing footnote if it exists
              showFootnote: !!footnoteText,
            })
          }
        >
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-3">
              {state.showBackground && (
                <ToggleButtonGroup
                  selectedKey={state.sourcesAndFootnoteDisplay ?? "none"}
                  onSelectionChange={(key) => {
                    if (key === "none") {
                      setState({
                        ...state,
                        sourcesAndFootnoteDisplay: null,
                      })
                    } else {
                      setState({
                        ...state,
                        sourcesAndFootnoteDisplay: key as
                          | "inside-card"
                          | "out-of-card",
                      })
                    }
                  }}
                  className="w-full"
                  size="small"
                >
                  <ToggleButton className="w-full" id="inside-card">
                    Inside
                  </ToggleButton>
                  <ToggleButton className="w-full" id="out-of-card">
                    Outside
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
              {state.sourcesAndFootnoteDisplay !== null && (
                <div className="flex flex-col gap-3">
                  <Checkbox
                    id="show-sources"
                    className="ml-1"
                    isSelected={state.showSources}
                    onChange={(checked) => {
                      setState((prev) => {
                        if (!checked && !prev.showFootnote) {
                          return {
                            ...prev,
                            showSources: false,
                            sourcesAndFootnoteDisplay: null, // Hide the entire section
                          }
                        }
                        return {
                          ...prev,
                          showSources: checked,
                        }
                      })
                    }}
                  >
                    Sources
                  </Checkbox>
                  <Checkbox
                    id="show-footnote"
                    className="ml-1"
                    isSelected={!footnoteText ? false : state.showFootnote}
                    onChange={(checked) => {
                      setState((prev) => {
                        if (!checked && !prev.showSources) {
                          return {
                            ...prev,
                            showFootnote: false,
                            sourcesAndFootnoteDisplay: null, // Hide the entire section
                          }
                        }
                        return {
                          ...prev,
                          showFootnote: checked,
                        }
                      })
                    }}
                    isDisabled={!footnoteText}
                  >
                    Footnote
                  </Checkbox>
                </div>
              )}
            </div>
          </div>
        </CollapsibleSection>
      )}
    </Card>
  )
}
